/**
 * Liste cross-tenant des collectivités — mode plateforme One For All Distribution.
 *
 * Affiche toutes les mairies clientes avec leurs stats (nb users, nb actifs,
 * nb admins, dernière activité). Filtre actif/inactif + recherche.
 *
 * Clic sur une ligne → page détail avec bouton "Entrer en mode hotline".
 *
 * Refonte R10 (mai 2026) :
 *   - En-tête de page avec LogoMark Building2 accent urgent (mode plateforme = sensible)
 *   - Card filtres glass avec search Input pl-10 + 1 select glass
 *   - Card table glass `padded={false}` avec rows hover surface/60
 *   - Lien "Détail" en text-urgent (signal action plateforme sensible)
 *
 * Logique métier 100% préservée :
 *   - api.platform.tenants.list paginé (PAGE_SIZE 50)
 *   - Filtres q (Enter→submit) + actif (changement → reload immédiat)
 *   - Badge état (Actif success / Suspendu warning) + Badge "Privé" neutral si visiblePublique=false
 *   - Stats par tenant : usersTotal (bold) + (usersActifs actifs · adminCount admin(s))
 *   - Lien Détail vers /platform/collectivites/:id
 */

import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Card, Badge, Button, Input, Alert, LogoMark, SectionEyebrow,
} from '@/components/ui';
import { Dialog } from '@/components/ui/Dialog';
import { api, ApiException } from '@/lib/api';
import { Search, Building2, AlertCircle, Plus } from 'lucide-react';
import type { PlatformTenantListItem } from '@roronoa-collectivites/shared-types';

const PAGE_SIZE = 50;

// Style partagé (focus urgent pour cohérence module platform)
const SELECT_CLS = 'h-10 rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass px-3 text-sm text-ink shadow-soft transition-all focus:border-urgent focus:outline-none focus:ring-2 focus:ring-urgent focus:ring-offset-2 focus:ring-offset-bg hover:border-border-strong';

export function TenantsListPage() {
  const [items, setItems] = useState<PlatformTenantListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [q, setQ] = useState('');
  const [qApplied, setQApplied] = useState('');
  const [actifFilter, setActifFilter] = useState<'all' | 'actif' | 'inactif'>('all');

  // Création de mairie (modal)
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const [cNom, setCNom] = useState('');
  const [cType, setCType] = useState<'commune' | 'epci' | 'syndicat'>('commune');
  const [cCp, setCCp] = useState('');
  const [cVille, setCVille] = useState('');
  const [cLoading, setCLoading] = useState(false);
  const [cError, setCError] = useState<string | null>(null);

  const fetchTenants = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.platform.tenants.list({
        page: 1,
        pageSize: PAGE_SIZE,
        q: qApplied || undefined,
        actif: actifFilter === 'all' ? undefined : actifFilter === 'actif',
      });
      setItems(res.items);
      setTotal(res.total);
    } catch (err) {
      if (err instanceof ApiException) setError(err.payload.error || 'Erreur de chargement');
      else setError('Erreur réseau');
    } finally {
      setLoading(false);
    }
  }, [qApplied, actifFilter]);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  function applySearch(e: React.FormEvent) {
    e.preventDefault();
    setQApplied(q);
  }

  function openCreate() {
    setCNom('');
    setCType('commune');
    setCCp('');
    setCVille('');
    setCError(null);
    setCreateOpen(true);
  }

  async function handleCreate(e?: React.FormEvent) {
    e?.preventDefault();
    if (cNom.trim().length < 2) {
      setCError('Le nom de la mairie doit faire au moins 2 caractères.');
      return;
    }
    if (!/^\d{5}$/.test(cCp)) {
      setCError('Le code postal doit comporter 5 chiffres.');
      return;
    }
    setCLoading(true);
    setCError(null);
    try {
      const created = await api.platform.tenants.create({
        nom: cNom.trim(),
        type: cType,
        codePostal: cCp,
        ville: cVille.trim() || undefined,
      });
      setCreateOpen(false);
      // On arrive directement sur la fiche de la nouvelle mairie : de là, le
      // super_admin peut entrer en mode hotline pour inviter les comptes.
      navigate(`/platform/collectivites/${created.id}`);
    } catch (err) {
      if (err instanceof ApiException) {
        setCError(err.payload.error || 'Création impossible');
      } else {
        setCError('Erreur réseau');
      }
    } finally {
      setCLoading(false);
    }
  }

  return (
    <section aria-labelledby="tenants-title" className="animate-fade-in">
      {/* En-tête : LogoMark accent urgent (platform = action sensible cross-tenant) */}
      <header className="mb-6 flex flex-wrap items-center gap-3">
        <LogoMark icon={Building2} accent="urgent" size="lg" />
        <div>
          <SectionEyebrow as="div">Plateforme One For All Distribution</SectionEyebrow>
          <h1
            id="tenants-title"
            className="mt-0.5 font-display text-xl font-semibold tracking-r-snug text-ink sm:text-2xl"
          >
            Collectivités
          </h1>
          <p className="mt-0.5 text-sm text-ink-muted">
            {total} collectivité{total > 1 ? 's' : ''} sur la plateforme One For All Distribution
          </p>
        </div>
        <Button variant="primary" size="md" onClick={openCreate} className="ml-auto">
          <Plus className="mr-1.5 h-4 w-4" aria-hidden="true" />
          Créer une mairie
        </Button>
      </header>

      {/* Filtres en Card glass */}
      <Card variant="glass" padded className="mb-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <form onSubmit={applySearch} className="flex flex-1 gap-2">
            <div className="relative flex-1">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-dim"
                aria-hidden="true"
              />
              <Input
                type="search"
                placeholder="Rechercher (nom, slug, code INSEE)"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                aria-label="Rechercher une collectivité"
                className="pl-10"
              />
            </div>
            <Button type="submit" variant="primary" size="md">
              Rechercher
            </Button>
          </form>
          <select
            value={actifFilter}
            onChange={(e) => setActifFilter(e.target.value as typeof actifFilter)}
            aria-label="Filtrer par état"
            className={SELECT_CLS}
          >
            <option value="all">Toutes</option>
            <option value="actif">Actives uniquement</option>
            <option value="inactif">Inactives uniquement</option>
          </select>
        </div>
      </Card>

      {error && (
        <Alert variant="error" icon={AlertCircle} className="mb-4">
          {error}
        </Alert>
      )}

      {/* Table glass */}
      <Card variant="glass" padded={false}>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="border-b border-border bg-surface/40 backdrop-blur-glass">
              <tr className="text-left">
                <th className="px-4 py-3 text-2xs uppercase tracking-r-eyebrow text-ink-subtle font-medium">Collectivité</th>
                <th className="px-4 py-3 text-2xs uppercase tracking-r-eyebrow text-ink-subtle font-medium">État</th>
                <th className="px-4 py-3 text-2xs uppercase tracking-r-eyebrow text-ink-subtle font-medium">Utilisateurs</th>
                <th className="px-4 py-3 text-2xs uppercase tracking-r-eyebrow text-ink-subtle font-medium">Dernière activité</th>
                <th className="px-4 py-3 text-right text-2xs uppercase tracking-r-eyebrow text-ink-subtle font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-ink-muted">
                    <div
                      className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-urgent border-t-transparent"
                      role="status"
                      aria-label="Chargement"
                    />
                    <p className="mt-3">Chargement…</p>
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-ink-muted">
                    Aucune collectivité ne correspond aux critères.
                  </td>
                </tr>
              ) : (
                items.map((t) => (
                  <tr
                    key={t.id}
                    className="border-b border-border last:border-b-0 transition-colors hover:bg-surface/60"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div
                          className="inline-grid h-8 w-8 place-items-center rounded-r-lg bg-urgent/15 text-urgent flex-shrink-0"
                          aria-hidden="true"
                        >
                          <Building2 className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium text-ink truncate">{t.nom}</div>
                          <div className="font-mono text-xs text-ink-muted truncate">
                            {t.slug}
                            {t.codeInsee ? ` · INSEE ${t.codeInsee}` : ''}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {t.actif ? (
                          <Badge variant="success" size="sm">Actif</Badge>
                        ) : (
                          <Badge variant="warning" size="sm">Suspendu</Badge>
                        )}
                        {!t.visiblePublique && (
                          <Badge variant="neutral" size="sm">Privé</Badge>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-ink-muted tabular-nums">
                      <strong className="text-ink">{t.stats.usersTotal}</strong>{' '}
                      <span className="text-2xs">({t.stats.usersActifs} actifs · {t.stats.adminCount} admin
                      {t.stats.adminCount > 1 ? 's' : ''})</span>
                    </td>
                    <td className="px-4 py-3 text-ink-muted">
                      {t.stats.lastActivityAt
                        ? new Date(t.stats.lastActivityAt).toLocaleString('fr-FR')
                        : '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link
                        to={`/platform/collectivites/${t.id}`}
                        className="inline-flex h-9 items-center rounded-r-md px-3 text-sm font-medium text-urgent transition-colors hover:bg-urgent/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-urgent focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
                      >
                        Détail
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Modal — création d'une mairie */}
      <Dialog
        open={createOpen}
        onClose={cLoading ? () => {} : () => setCreateOpen(false)}
        title="Créer une mairie"
        description="La mairie sera créée active et visible. Vous arriverez sur sa fiche pour y entrer et inviter les comptes."
        variant="info"
        hideCloseButton={cLoading}
        footer={
          <>
            <Button
              variant="secondary"
              size="md"
              onClick={() => setCreateOpen(false)}
              disabled={cLoading}
            >
              Annuler
            </Button>
            <Button
              variant="primary"
              size="md"
              onClick={() => handleCreate()}
              loading={cLoading}
              disabled={cLoading || cNom.trim().length < 2 || !/^\d{5}$/.test(cCp)}
            >
              Créer la mairie
            </Button>
          </>
        }
      >
        <form onSubmit={handleCreate} className="space-y-4">
          {cError && (
            <Alert variant="error" icon={AlertCircle}>
              {cError}
            </Alert>
          )}
          <div>
            <label
              htmlFor="create-mairie-nom"
              className="mb-1 block text-sm font-medium text-ink"
            >
              Nom de la mairie <span className="text-urgent">*</span>
            </label>
            <Input
              id="create-mairie-nom"
              value={cNom}
              onChange={(e) => setCNom(e.target.value)}
              placeholder="Mairie de Saint-Nazaire-sur-Charente"
              maxLength={255}
              autoFocus
            />
          </div>
          <div>
            <label
              htmlFor="create-mairie-type"
              className="mb-1 block text-sm font-medium text-ink"
            >
              Type de collectivité
            </label>
            <select
              id="create-mairie-type"
              value={cType}
              onChange={(e) => setCType(e.target.value as typeof cType)}
              className={`${SELECT_CLS} w-full`}
            >
              <option value="commune">Commune</option>
              <option value="epci">EPCI (intercommunalité)</option>
              <option value="syndicat">Syndicat</option>
            </select>
          </div>
          <div>
            <label
              htmlFor="create-mairie-cp"
              className="mb-1 block text-sm font-medium text-ink"
            >
              Code postal <span className="text-urgent">*</span>
            </label>
            <Input
              id="create-mairie-cp"
              inputMode="numeric"
              value={cCp}
              onChange={(e) => setCCp(e.target.value.replace(/\D/g, '').slice(0, 5))}
              placeholder="17780"
              maxLength={5}
            />
            <p className="mt-1 text-xs text-ink-subtle">
              Sert à retrouver la mairie dans le formulaire d'inscription des administrés.
            </p>
          </div>
          <div>
            <label
              htmlFor="create-mairie-ville"
              className="mb-1 block text-sm font-medium text-ink"
            >
              Ville <span className="text-ink-subtle">(optionnel)</span>
            </label>
            <Input
              id="create-mairie-ville"
              value={cVille}
              onChange={(e) => setCVille(e.target.value)}
              placeholder="Saint-Nazaire-sur-Charente"
              maxLength={100}
            />
          </div>
          <p className="text-xs text-ink-subtle">
            L'adresse, le SIRET et les autres coordonnées se renseignent ensuite
            depuis la page Mairie de la collectivité.
          </p>
        </form>
      </Dialog>
    </section>
  );
}
